// ============================================================
// TIMEZONE-AWARE SCHEDULE CALCULATION (frontend)
// ============================================================
//
// Dependency-free (uses only the standard Intl API already available in
// every supported browser) so no new package is needed on the client.
//
// InsightPulse schedules a report for exactly ONE send — a specific
// calendar date + time in the user's chosen IANA time zone. This file
// converts that single wall-clock moment to a UTC Date so it can be
// stored as `scheduledAt` and compared against "now" by the backend
// worker. There is no recurrence math here anymore — reports are
// scheduled once, not repeated (see IMPLEMENTATION_ONE_TIME_SCHEDULING.md).

export function detectBrowserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

// Returns the UTC offset (in minutes, e.g. -300 for EST) of `timeZone` at
// the given UTC instant `date`, using Intl instead of a date library.
function offsetMinutesAt(date, timeZone) {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  const parts = dtf.formatToParts(date).reduce((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});

  const asUTC = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  );

  return (asUTC - date.getTime()) / 60000;
}

// Converts a wall-clock date/time (as it would read on a clock in
// `timeZone`) into the equivalent UTC Date instance. Handles DST by
// resolving the offset iteratively (two passes is always enough in
// practice for standard IANA zones).
export function zonedTimeToUtc(year, month, day, hour, minute, timeZone) {
  const naiveUtc = new Date(Date.UTC(year, month, day, hour, minute, 0));
  const offset1 = offsetMinutesAt(naiveUtc, timeZone);
  const guess1 = new Date(naiveUtc.getTime() - offset1 * 60000);
  const offset2 = offsetMinutesAt(guess1, timeZone);
  return new Date(naiveUtc.getTime() - offset2 * 60000);
}

/**
 * Converts a one-time schedule's chosen date/time/timezone into a UTC
 * Date — this is `scheduledAt`, the single instant the report will be
 * generated and sent at.
 *
 * @param {Object} opts
 * @param {string} opts.date "YYYY-MM-DD"
 * @param {string} opts.time "HH:mm" (24-hour, local to `timezone`)
 * @param {string} opts.timezone IANA time zone identifier
 * @returns {Date|null} null if date/time are missing or unparsable
 */
export function computeScheduledAt({ date, time, timezone }) {
  if (!date || !time) return null;

  const [year, month, day] = date.split("-").map(Number);
  const [hour, minute] = time.split(":").map(Number);

  if ([year, month, day, hour, minute].some((n) => Number.isNaN(n))) return null;

  return zonedTimeToUtc(year, month - 1, day, hour, minute, timezone || "UTC");
}
